// Cached Requests
const cachedRequests = new Map<string, unknown>();
// resolved on <TimeStamp> used to measure freshness
const cacheTimestamps = new Map<string, number>();
/*
  Currently running requests, used to deduplicate requests.
  as in if a request with the same cache key is running don't
  run it again just store it and resolve when the first call
  with this key is resolved
*/
const runningRequests = new Map<
  string,
  // eslint-disable-next-line
  Array<{ resolve: (res: any) => void; reject: (rej: any) => void }>
>();

export async function cacheRequest<T>(
  cacheKey: string, //Argument supplied by the user to make requests unique (suggested format: `<functionName>__<Arguments1>-<Argument2>--extraConditions`)
  load: () => Promise<T>, //The function that is being cached
  freshness = 200 //How fresh should the data be in millisecs
): Promise<T> {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const now = new Date().getTime();
    //Do we already have the data in our cache?
    if (cachedRequests.has(cacheKey)) {
      // if we do then we check the freshness
      const cacheFreshness = cacheTimestamps.get(cacheKey);
      const fresh = cacheFreshness ? cacheFreshness + freshness > now : false;
      if (fresh) {
        // if fresh as per the freshness argument we return it
        resolve(cachedRequests.get(cacheKey) as T);
        return;
      }
    }
    // if not in our cache then we try executing the function
    // first we check if there is a running request already
    if (runningRequests.has(cacheKey)) {
      // if so we store the resolve and reject function of this promise but don't execute the load function
      runningRequests.get(cacheKey)?.push({ resolve, reject });
    } else {
      // if no we store the resolve and reject but execute the load
      runningRequests.set(cacheKey, [{ resolve, reject }]);
      try {
        const response = await load();
        // once the promise resolved we save the data in our cache
        cachedRequests.set(cacheKey, response);
        // we set the timestamp of this data
        cacheTimestamps.set(cacheKey, now);
        // and we resolve all the pending promises for this cacheKey using the resolve function stored
        runningRequests.get(cacheKey)?.forEach(({ resolve }) => {
          resolve(response as T);
        });
      } catch (e) {
        // if the promise rejects we reject all the promises currently stored for this cacheKey
        runningRequests.get(cacheKey)?.forEach(({ reject }) => {
          reject(e);
        });
      }
      // once a pending request resolves or rejects we remove it from the running requests pool
      runningRequests.delete(cacheKey);
    }
  });
}
